/*
 * Copyright (c) 2023, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React from 'react'
import {useRouteMatch} from 'react-router'
import {Stack} from '@salesforce/retail-react-app/app/components/shared/ui'
import {useOrder} from '@salesforce/commerce-sdk-react'
import Order from './order'

const onClient = typeof window !== 'undefined'

/**
 * AccountOrderDetail is a React functional component that renders the details of a specific order
 * for a user's account. It utilizes various hooks and components to fetch and display order information,
 * including shipping and billing addresses, payment methods, and order items.
 *
 * The component uses the `useOrder` hook to fetch order data based on the order number obtained from
 * the route parameters. It also handles loading states and accessibility features, such as focusing
 * on the "Order Details" header when the component mounts.
 *
 * The component structure includes:
 * - A back button to navigate to the order history page.
 * - Display of order details such as order number, ordered date, and shipping status.
 * - Sections for shipping method, shipping address, payment method, and billing address.
 * - An invoice link for the order.
 * - A summary of the order items and their count.
 *
 * The component uses internationalization (i18n) for text content, ensuring that messages are
 * formatted according to the user's locale.
 *
 * @component
 * @returns {JSX.Element} The rendered component displaying order details.
 */
const AccountOrderDetail = () => {
    const {params} = useRouteMatch()

    const {data: order, isLoading: isOrderLoading} = useOrder(
        {
            parameters: {orderNo: params.orderNo}
        },
        {
            enabled: onClient && !!params.orderNo
        }
    )
    const isLoading = isOrderLoading || !order

    return (
        <Stack spacing={24} data-testid="account-order-details-page">
            <Order order={order} isLoading={isLoading} />
        </Stack>
    )
}

AccountOrderDetail.getTemplateName = () => 'account-order-history'

export default AccountOrderDetail
